import * as React from 'react';
import { useEffect, useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import { Close, Done, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { CircularProgress } from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useSearchParams } from "react-router-dom";
import DebounceInput from "../../domain/DebounceInput";
import RecurringBookingsModal from "../modals/RecurringBookingsModal";
import ApiService from "../../axios/Bookings-Axios";
import ResponsiveTableWrapper, { ResponsiveFilterBar, FilterBarLeft, FilterBarRight } from "../ui/ResponsiveTableWrapper";

interface Service {
    name: string;
}
interface ServicePrice {
    price: number;
}
interface Row {
    id: number;
    frequencyInterval: string;
    service: Service;
    service_price: ServicePrice;
    interval: string;
    frequency: string;
    created_at: string;
    active: boolean;
    expanded_row: RowExpanded;
}
interface RowExpanded {
    full_name: string;
    street_address: string;
    postcode: string;
    phone: string;
    notes: string;
    house_type: string;
    id: string;
    email: string;
}

function formatDate(old_date: string | Date): string {
    const date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
    }
    return 'No Valid Date';
}

function Row(props: { row: Row; initialOpen?: boolean, onRowClick: (rowId: number) => void }) {
    const { row } = props;
    const [open, setOpen] = useState(props.initialOpen || false);

    const handleClick = () => {
        props.onRowClick(row.id);
    };

    return (
        <React.Fragment>
            <tr>
                <td><IconButton aria-label="expand row" variant="plain" color="neutral" size="sm" onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></td>
                <td>{formatDate(row.created_at)}</td>
                <td>{row.service.name}</td>
                <td>{row.expanded_row.full_name}</td>
                <td>£{row.service_price.price}</td>
                <td>Every {row.frequencyInterval}s</td>
                <td>{row.active ? <Done color="success" /> : <Close color="action" />}</td>
                <td><Button size="sm" color="primary" onClick={handleClick}>Edit Recurring Booking</Button></td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={8}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Typography level="body-lg" component="div">Customer Information</Typography>
                            <Table borderAxis="bothBetween" aria-label="customer details">
                                <thead><tr><th>Full Name</th><th>Number</th><th>Email</th><th>Street Address</th><th>Postcode</th></tr></thead>
                                <tbody><tr><td>{row.expanded_row.full_name}</td><td>{row.expanded_row.phone}</td><td>{row.expanded_row.email}</td><td>{row.expanded_row.street_address}</td><td>{row.expanded_row.postcode}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={8}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Table borderAxis="bothBetween" aria-label="notes">
                                <thead><tr><th>Notes</th></tr></thead>
                                <tbody><tr><td style={{ whiteSpace: 'pre-wrap' }}>{row.expanded_row.notes || 'No notes'}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
        </React.Fragment>
    );
}

const mapRawDataToRow = (data: any[]): Row[] => {
    return data.map(item => ({
        id: item.id,
        frequencyInterval: `${item.interval} ${item.frequency}`,
        service: item.service,
        service_price: item.service_price,
        interval: item.interval,
        frequency: item.frequency,
        created_at: item.created_at,
        active: item.active,
        expanded_row: {
            full_name: item.customer.full_name,
            street_address: item.customer.street_address,
            postcode: item.customer.postcode,
            phone: item.customer.phone,
            notes: item.customer.notes,
            house_type: item.customer.house_type?.name || '',
            id: item.customer.id.toString(),
            email: item.customer.email,
        },
    }));
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export default function RecurringBookingsTable() {
    const [loadedData, setLoadingData] = useState(false);
    const [rows, setRows] = useState<Row[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [urlSearchParam, setUrlSearchParam] = useSearchParams();
    const [openRecurringBookingId, setOpenRecurringBookingId] = useState(0);
    const [openRecurringBookingModal, setOpenRecurringBookingModal] = useState(false);

    const handleCallback = () => {
        setOpenRecurringBookingModal(false);
    };

    const handleOpenRecurringBookingModal = (rowId: number) => {
        setOpenRecurringBookingModal(true);
        setOpenRecurringBookingId(rowId);
    };

    const handleNewOpenRecurringBookingModal = () => {
        handleOpenRecurringBookingModal(-1);
    };

    const getLabelDisplayedRowsTo = () => {
        return Math.min(pageCount, (page + 1) * rowsPerPage);
    };

    useEffect(() => {
        setLoadingData(false);
        const fetchData = async () => {
            try {
                const bookingsData = await ApiService.getRecurringBookings({ page: page + 1, search });
                setRows(mapRawDataToRow(bookingsData.results || []));
                setPageCount(bookingsData.count || 0);
            } catch (error) {
                console.error("Failed to fetch recurring bookings:", error);
                setRows([]);
                setPageCount(0);
            } finally {
                setLoadingData(true);
            }
        };
        fetchData();
    }, [page, search]);

    const handleDebounce = (value: string) => {
        setPage(0);
        setSearch(value);
    };

    return (
        <>
            <ResponsiveFilterBar>
                <FilterBarLeft>
                    <DebounceInput 
                        sx={{ width: { xs: '100%', sm: '250px' } }} 
                        startDecorator={<SearchRoundedIcon />}
                        placeholder="Search by any field..."
                        debounceTimeout={1000}
                        defaultValue={urlSearchParam.get("search") as string}
                        handleDebounce={handleDebounce}
                    />
                </FilterBarLeft>
                <FilterBarRight>
                    <Button onClick={handleNewOpenRecurringBookingModal}>
                        Create New Recurring Booking
                    </Button>
                </FilterBarRight>
            </ResponsiveFilterBar>
            <ResponsiveTableWrapper minWidth={900}>
                <Table hoverRow aria-label="collapsible table">
                    <thead>
                    <tr>
                        <th style={{ width: 40 }} aria-label="empty" />
                        <th style={{ width: '15%' }}>Created At</th>
                        <th>Service</th>
                        <th>Customer Name</th>
                        <th>Service Price</th>
                        <th>How Often</th>
                        <th>Active</th>
                        <th aria-label="last" />
                    </tr>
                    </thead>
                    <tbody>
                    {loadedData ? (
                        rows.map((row) => (
                            <Row key={row.id} row={row} initialOpen={search.length > 0} onRowClick={handleOpenRecurringBookingModal} />
                        ))
                    ) : (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                    )}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colSpan={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                    {labelDisplayedRows({
                                        from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                        to: getLabelDisplayedRowsTo(),
                                        count: pageCount,
                                    })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                                </Box>
                            </Box>
                        </td>
                    </tr>
                    </tfoot>
                </Table>
            </ResponsiveTableWrapper>
            <React.Fragment>

            <RecurringBookingsModal key={'recurring-bookings-modal-' + openRecurringBookingModal}
                                    openState={openRecurringBookingModal}
                                    recurringBookingId={openRecurringBookingId} parentCallback={handleCallback} />
            </React.Fragment>
            </>
    );
}